import { useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ContentCopyIcon from "@/components/ui/icon/content-copy";
import EditDocumentIcon from "@/components/ui/icon/edit-document";
import OpenInNewIcon from "@/components/ui/icon/open-in-new";
import ThreeDotsHorizontal from "@/components/ui/icon/three-dots-horizontal";

import type { MouseEvent } from "react";

export type ListIconButtonsProps = {
	onClickEditButton?: () => void;

	onClickCopyButton?: () => void;
	onClickDeleteButton?: () => void;
	onClickOpenButton?: () => void;
	/** ゲスト向け公開URLをクリップボードへコピーする（シークレットプラン等で使用） */
	onClickCopyGuestUrlButton?: () => void;
};

/**
 * @todo composition パターンによるリファクタリングを行う
 */
export const IconButtonList = ({
	onClickEditButton,
	onClickCopyButton,
	onClickDeleteButton,
	onClickOpenButton,
	onClickCopyGuestUrlButton,
}: ListIconButtonsProps) => {
	const [isShowButtonIconList, setIsShowButtonIconList] = useState(false);

	const canShowIconButtonList = useMemo(
		() =>
			Boolean(onClickCopyButton) ||
			Boolean(onClickDeleteButton) ||
			Boolean(onClickOpenButton) ||
			Boolean(onClickEditButton) ||
			Boolean(onClickCopyGuestUrlButton),
		[onClickCopyButton, onClickDeleteButton, onClickOpenButton, onClickEditButton, onClickCopyGuestUrlButton],
	);

	const handleClickEditIcon = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onClickEditButton && onClickEditButton();
		},
		[onClickEditButton],
	);
	const handleClickOpenButton = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onClickOpenButton && onClickOpenButton();
		},
		[onClickOpenButton],
	);
	const handleClickCopyButton = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onClickCopyButton && onClickCopyButton();
		},
		[onClickCopyButton],
	);
	const handleClickDeleteButton = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onClickDeleteButton && onClickDeleteButton();
		},
		[onClickDeleteButton],
	);
	const handleClickCopyGuestUrlButton = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onClickCopyGuestUrlButton && onClickCopyGuestUrlButton();
		},
		[onClickCopyGuestUrlButton],
	);

	const handleClick = useCallback(() => {
		setIsShowButtonIconList(!isShowButtonIconList);
	}, [isShowButtonIconList]);

	return (
		<>
			{canShowIconButtonList ? (
				<div className="flex gap-x-2">
					{onClickEditButton && (
						<Button
							type="button"
							variant={"input-ish"}
							size={"icon"}
							tooltip="編集"
							onClick={handleClickEditIcon}
						>
							<EditDocumentIcon size={"sm"} />
						</Button>
					)}
					{onClickCopyGuestUrlButton && (
						<Button
							type="button"
							variant={"input-ish"}
							size={"icon"}
							tooltip="URLをコピーする"
							onClick={handleClickCopyGuestUrlButton}
						>
							<ContentCopyIcon size={"sm"} />
						</Button>
					)}
					{onClickOpenButton && (
						<Button
							variant={"input-ish"}
							size={"icon"}
							tooltip="表示を確認"
							onClick={handleClickOpenButton}
						>
							<OpenInNewIcon size={"sm"} />
						</Button>
					)}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button type="button" variant={"input-ish"} size={"icon"} onClick={handleClick}>
								<ThreeDotsHorizontal size={"sm"} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-40">
							<div className="grid space-y-1 text-left">
								{onClickCopyButton && (
									<Button
										type="button"
										variant={"no-outline"}
										textAlign={"left"}
										onClick={handleClickCopyButton}
									>
										複製する
									</Button>
								)}
								{onClickDeleteButton && (
									<Button
										type="button"
										variant={"no-outline"}
										textAlign={"left"}
										onClick={handleClickDeleteButton}
									>
										<p className="font-medium text-error-600">削除する</p>
									</Button>
								)}
							</div>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			) : null}
		</>
	);
};
